import React, { createContext, startTransition, useContext, useLayoutEffect, useRef, useState } from 'react';

const CountryHoverContext = createContext(null);

/**
 * Holds world-map country hover in a small React tree so WarEffortFortification
 * (and WorldMap) do not re-render on every country crossing.
 *
 * Position is written to the tooltip DOM. React state updates only when the
 * hovered country changes, and those updates are startTransition so they do
 * not block pointer movement.
 */
export function CountryHoverProvider({ bindRef, children }) {
  if (!bindRef) {
    throw new Error('CountryHoverProvider: bindRef is required');
  }
  const [cellId, setCellId] = useState(null);
  const setCellIdRef = useRef(setCellId);
  const cellIdRef = useRef(null);
  const positionElRef = useRef(null);
  const lastPositionRef = useRef(null);
  setCellIdRef.current = setCellId;

  const writePosition = (position) => {
    lastPositionRef.current = position;
    const el = positionElRef.current;
    if (!el) return;
    el.style.left = `${position.x}px`;
    el.style.top = `${position.y}px`;
    const maxHeight = Math.max(200, window.innerHeight - position.y - 12);
    el.style.maxHeight = `min(calc(100vh - 24px), ${maxHeight}px)`;
  };

  bindRef.current = {
    show(nextCellId, position) {
      if (nextCellId == null) {
        throw new Error('CountryHoverProvider.show: cellId is required');
      }
      if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
        throw new Error('CountryHoverProvider.show: position {x,y} is required');
      }
      writePosition(position);
      if (cellIdRef.current === nextCellId) return;
      cellIdRef.current = nextCellId;
      startTransition(() => {
        setCellIdRef.current(nextCellId);
      });
    },
    hide() {
      cellIdRef.current = null;
      setCellIdRef.current(null);
    },
    setPositionEl(el) {
      positionElRef.current = el;
      if (el && lastPositionRef.current) {
        writePosition(lastPositionRef.current);
      }
    }
  };

  useLayoutEffect(() => {
    return () => {
      bindRef.current = null;
    };
  }, [bindRef]);

  return (
    <CountryHoverContext.Provider value={cellId}>
      {children}
    </CountryHoverContext.Provider>
  );
}

export function useCountryHover() {
  return useContext(CountryHoverContext);
}

export function WarEffortCountryHoverTooltip({ bindRef, children }) {
  if (!bindRef) {
    throw new Error('WarEffortCountryHoverTooltip: bindRef is required');
  }
  if (typeof children !== 'function') {
    throw new Error('WarEffortCountryHoverTooltip: children must be a render function');
  }
  const cellId = useCountryHover();
  if (cellId == null) return null;
  return children(cellId, (el) => {
    if (bindRef.current && typeof bindRef.current.setPositionEl === 'function') {
      bindRef.current.setPositionEl(el);
    }
  });
}
